"use client";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import "./index.css";
import { BestServiceData, Program, ResponseObject } from "@/app/classes/main";
import { ApiService } from "@/app/services/AppService";
import ViewDetails from "../genericComponents/view-details/ViewDetails";
import { AddToCartService } from "../../services/cart";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";

export const BestSellingPackage = ({}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isXtraSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [bestServiceData, setBestServiceData] = useState<BestServiceData>({
    generalServicePackageResponses: [],
  });
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Program | undefined>(undefined);
  const [reloadFlag, setReloadFlag] = useState(false);
  const [reloadFlagFromViewCart, setReloadFlagFromViewCart] = useState(false);
  const [dynamicStyle, setDynamicStyle] = useState("");
  const apiService = useMemo(() => new ApiService(), []);
  const addTocartService = useMemo(() => new AddToCartService(), []);
  const currencyCode = useCurrencyCode();
  const xHubId = getHubId();
  const [screenWidth, setScreenWidth] = useState(0);

  useEffect(() => {
    // Check if we are in the browser (client-side)
    if (typeof window !== "undefined") {
      // Get the screen width when the component mounts (only on client-side)
      setScreenWidth(window.innerWidth);
    }
  }, []);

  const handleAddGift = async (services: any, e: any, index: number) => {
    e.stopPropagation();
    var serviceAvailableIncart = addTocartService.checkIfAvailableinCart(
      services?.id,
      services?.cid
    );
    if (serviceAvailableIncart) {
      await addTocartService.addToCart(
        services?.id,
        false,
        services?.cid,
        services?.coverImage?.secureHttpPath,
        services?.aboutList,
        services?.subHeading,
        services?.serviceCharge,
        services?.productCost,
        services?.min,
        services?.specialPrice,
        services?.specialMessage
      );
    } else {
      await addTocartService.addToCart(
        services?.id,
        true,
        services?.cid,
        services?.coverImage?.secureHttpPath,
        services?.aboutList,
        services?.subHeading,
        services?.serviceCharge,
        services?.productCost,
        services?.min,
        services?.specialPrice,
        services?.specialMessage
      );
    }
    // Create a new copy of bestServiceData with updated inCart value
    const newBestServiceData = { ...bestServiceData };
    newBestServiceData.generalServicePackageResponses =
      bestServiceData.generalServicePackageResponses.map(
        (service: any, idx: any) => {
          if (idx === index) {
            return {
              ...service,
              inCart: addTocartService.checkIfAvailableinCart(
                services?.id,
                services?.cid
              ),
            };
          }
          return service;
        }
      );
    setBestServiceData(newBestServiceData);
    setReloadFlag(true);
  };

  const handleOpenViewDetails = (item: any) => {
    setData(item);
    setOpen(true);
  };

  const handleCardPosition = useCallback(() => {
    if (
      (bestServiceData &&
        bestServiceData?.generalServicePackageResponses?.length > 5) ||
      isSmallScreen ||
      screenWidth < 1425
    ) {
      setDynamicStyle("left");
    } else {
      setDynamicStyle("center");
    }
  }, [bestServiceData, isSmallScreen, screenWidth]);

  useEffect(() => {
    handleCardPosition();
  }, [isSmallScreen, handleCardPosition]);

  const getBestSellingPackage = useCallback(async () => {
    try {
      const responseObject: ResponseObject | undefined =
        await apiService.get<ResponseObject>(
          apiService.bestSellingPackage,
          undefined,
          xHubId
        );
      var serviceData = responseObject?.object;
      serviceData?.generalServicePackageResponses?.forEach((spr: any) => {
        spr.inCart = addTocartService.checkIfAvailableinCart(
          spr?.services?.id,
          spr?.services?.cid
        );
        // console.log("servicePackageResponse.inCart " + spr.inCart);
      });
      setBestServiceData(serviceData);
      setReloadFlagFromViewCart(false);
    } catch (error) {
      console.log("Error:", error);
    }
  }, [addTocartService, xHubId, apiService]);

  useEffect(() => {
    getBestSellingPackage();
  }, [reloadFlagFromViewCart, getBestSellingPackage]);

  return (
    <>
      {bestServiceData?.generalServicePackageResponses?.length > 0 ? (
        <Box
          sx={{
            marginTop: 0,
            width: "var(--salontym-content-width)",
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "var(--salontym-section-gap)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(246,251,248,0.92) 100%)",
            padding: "1.25rem 0px 1rem",
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: "28px", sm: "34px" },
            border: "1px solid rgba(18,53,36,0.08)",
            boxShadow: "0 20px 60px rgba(18,53,36,0.08)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-4rem",
              right: "-3rem",
              width: "14rem",
              height: "14rem",
              borderRadius: "999px",
              background:
                "radial-gradient(circle, rgba(223,246,232,0.8) 0%, rgba(223,246,232,0) 70%)",
            }}
          />
          <Grid item container xs={12} sm={12} md={12} lg={12} margin="auto">
            <Box
              sx={{
                textAlign: "center",
                flexDirection: "column",
                alignItems: "center",
                width: "100% ",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: "black",
                  display: "grid",
                  justifyContent: "center",
                  alignItems: "start",
                  margin: "0.25rem auto 1.6rem",
                  padding: "0 1rem",
                  gap: "0.6rem",
                }}
              >
                <Box className="section-eyebrow" sx={{ margin: "0 auto" }}>
                  Curated favorites
                </Box>
                <Typography
                  sx={{
                    fontFamily: "var(--salontym-font-display)",
                    color: "#102117",
                    fontSize: { xs: "2rem", sm: "2.8rem" },
                    lineHeight: 1.02,
                    maxWidth: "15ch",
                    margin: "0 auto",
                  }}
                >
                  Best-selling packages, made to convert faster
                </Typography>
                <Typography
                  sx={{
                    color: "#5e6d63",
                    maxWidth: "42rem",
                    margin: "0 auto",
                    lineHeight: 1.75,
                  }}
                >
                  High-performing combinations with clear value, social proof,
                  and a cleaner premium presentation.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: dynamicStyle,
                  margin: "auto",
                  overflowX: "scroll",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {bestServiceData?.generalServicePackageResponses?.map(
                  (item: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        flexWrap: "nowrap",
                        minWidth: "17rem",
                        minHeight: "21rem",
                        width: "17rem",
                        height: "21rem",
                        borderRadius: "1.35rem",
                        overflow: "hidden",
                        position: "relative",
                        margin: { xs: "0.5rem", sm: "1rem" },
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,247,235,0.95) 100%)",
                        cursor: "pointer",
                        boxShadow: "0 18px 36px rgba(18,53,36,0.12)",
                        border: "1px solid rgba(18,53,36,0.08)",
                      }}
                      onClick={() => handleOpenViewDetails(item)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "left",
                          alignItems: "start",
                          padding: "8px",
                        }}
                      >
                        <Image
                          src={item?.services?.coverImage?.secureHttpPath}
                          width={1000}
                          height={1000}
                          style={{
                            margin: "0px",
                            objectFit: "cover",
                            width: "3.5rem",
                            minWidth: "3.5rem",
                            height: "3.5rem",
                            borderRadius: "10rem",
                            boxShadow: "rgba(0, 0, 0, 0.3) 0px 0px 5px 1px",
                          }}
                          alt="category"
                        />
                        <Box
                          sx={{
                            marginLeft: "1rem",
                            textAlign: "left",
                            lineHeight: "0px",
                            maxHeight: "4em",
                            overflow: "auto",
                            "&:: -webkit-scrollbar": {
                              display: "none",
                            },
                          }}
                        >
                          <Typography variant="body2">
                            {item?.services?.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "gray" }}>
                            {item?.services?.subHeading}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          textAlign: "left",
                          padding: "8px",
                          width: "-webkit-fill-available",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "left",
                            alignItems: "center",
                            color: "black",
                          }}
                        >
                          <StarIcon
                            sx={{ fontSize: "16px", color: "#15741a" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ paddingLeft: "8px", color: "black" }}
                          >
                            <span
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              {item?.rating}
                            </span>{" "}
                            &nbsp;
                            <span
                              style={{
                                textDecoration: "underline",
                                fontWeight: "bold",
                                fontSize: "11px",
                              }}
                            >
                              ({item?.reviewCount} Reviews)
                            </span>{" "}
                            &nbsp;&nbsp;
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "left",
                              alignItems: "center",
                              color: "white",
                              border: "1px solid #15741a",
                              background: "#15741a",
                              padding: "2px 4px",
                              borderRadius: "4px",
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: "16px" }} />
                            <Typography
                              variant="caption"
                              sx={{ paddingLeft: "4px" }}
                            >
                              {item?.viewCount}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            maxHeight: "9rem",
                            marginTop: "0.5rem",
                            overflow: "scroll",
                            "&:: -webkit-scrollbar": {
                              display: "none",
                            },
                          }}
                        >
                          {item?.services?.aboutList?.map(
                            (nestedItem: any, index: number) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "start",
                                }}
                                key={index}
                              >
                                <CheckIcon
                                  sx={{
                                    color: "#15741a",
                                    paddingRight: "5px",
                                    fontSize: "1rem",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "black" }}
                                >
                                  {nestedItem}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            bottom: 0,
                            left: 0,
                            position: "absolute",
                            margin: "0.5rem",
                            width: "-webkit-fill-available",
                          }}
                        >
                          <Box>
                            <Typography variant="body1">
                              <span
                                style={{
                                  color: "#15741a",
                                  fontWeight: "bold",
                                }}
                              >
                                {currencyCode}
                                {item?.services?.amount}/-
                              </span>{" "}
                              &nbsp; &nbsp;
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  color: "gray",
                                  fontSize: "14px",
                                }}
                              >
                                {currencyCode}
                                {item?.services?.mrp}/-
                              </span>
                            </Typography>
                          </Box>
                          <Box>
                            {!item?.inCart ? (
                              <AddIcon
                                fontSize="small"
                                style={{
                                  height: "1.8rem",
                                  width: "1.8rem",
                                  color: "#15741a",
                                  background: "white",
                                  padding: "4px",
                                  borderRadius: "10rem",
                                  cursor: "pointer",
                                  boxShadow:
                                    "rgba(0, 0, 0, 0.3) 0px 0px 5px 1px",
                                }}
                                onClick={(e) =>
                                  handleAddGift(item.services, e, index)
                                }
                              />
                            ) : (
                              <CheckIcon
                                fontSize="small"
                                style={{
                                  height: "1.8rem",
                                  width: "1.8rem",
                                  color: "#15741a",
                                  background: "white",
                                  padding: "4px",
                                  borderRadius: "10rem",
                                  cursor: "pointer",
                                  boxShadow:
                                    "rgba(0, 0, 0, 0.3) 0px 0px 5px 1px",
                                }}
                                onClick={(e) =>
                                  handleAddGift(item.services, e, index)
                                }
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          </Grid>
          <ViewDetails
            open={open}
            setClose={() => setOpen(false)}
            imageUrl={
              data?.services?.banner?.secureHttpPath ??
              data?.services?.coverImage?.secureHttpPath
            }
            heading={data?.services?.name}
            id={data?.services?.id}
            cid={data?.services?.cid}
            servicedDetails={data?.services?.aboutList}
            descriptions={data?.services?.descriptions}
            amount={data?.services?.amount}
            min={data?.services?.min}
            subHeading={data?.services?.subHeading}
            mrp={data?.services?.mrp}
            coin={data?.services?.cashbackSalontymCoins}
            aboutList={data?.services?.aboutList}
            serviceCharge={data?.services?.serviceCharge}
            productCost={data?.services?.productCost}
            specialPrice={data?.services?.specialPrice}
            specialMessage={data?.services?.specialMessage}
            reloadCartItemsFlag={() => {
              setReloadFlagFromViewCart(true);
            }}
            setReloadViewDetailsItem={() => setReloadFlag(false)}
            reloadViewDetailsItem={reloadFlag}
          />
        </Box>
      ) : (
        ""
      )}
    </>
  );
};
